import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, Eye, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function md(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  lines.forEach((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) {
      out.push(
        <h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-6 mb-2 tracking-wide">
          {l.slice(3)}
        </h3>,
      );
    } else if (l.startsWith("# ")) {
      out.push(
        <h2 key={k} className="font-heading text-2xl text-[#E6E1D6] mt-6 mb-3">
          {l.slice(2)}
        </h2>,
      );
    } else if (l.startsWith("> ")) {
      out.push(
        <blockquote key={k} className="border-l-2 border-[#D4AF37]/50 pl-4 my-3 italic text-[#D4AF37] font-light">
          {l.slice(2)}
        </blockquote>,
      );
    } else if (l.startsWith("- ") || l.startsWith("* ")) {
      out.push(
        <li key={k} className="text-sm text-[#E6E1D6] ml-6 list-disc marker:text-[#D4AF37]">
          {mdInline(l.slice(2))}
        </li>,
      );
    } else if (!l.trim()) {
      out.push(<div key={k} className="h-2" />);
    } else {
      out.push(
        <p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light my-1.5">
          {mdInline(l)}
        </p>,
      );
    }
  });
  return out;
}
function mdInline(text) {
  // Bold and italic in inline text
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <b key={i} className="text-[#D4AF37]">{p.slice(2, -2)}</b>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function AtmaDarshanPanel({ profileId }) {
  const { lang } = useLang();
  const [themes, setThemes] = useState([]);
  const [themeKey, setThemeKey] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/atma-darshan/themes`)
      .then((r) => setThemes(r.data.themes || []))
      .catch(() => {});
  }, []);

  const open = useCallback(async (key) => {
    if (!profileId) return;
    setThemeKey(key); setData(null); setLoading(true);
    try {
      const r = await axios.get(
        `${API}/atma-darshan/${profileId}/reading`,
        { params: { theme: key, lang } },
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reading load failed");
    } finally { setLoading(false); }
  }, [profileId, lang]);

  return (
    <div className="space-y-6" data-testid="atma-darshan-panel">
      {/* Intro banner */}
      <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#151821] to-[#0F1115] p-6 rounded-sm">
        <div className="flex items-center gap-3 mb-2">
          <Eye size={20} className="text-[#D4AF37]" />
          <div className="font-heading text-2xl text-[#D4AF37] tracking-wider">Atma Darshan</div>
          <span className="text-[10px] uppercase tracking-widest font-mono text-[#A8896C] border border-[#A8896C]/40 px-2 py-0.5">Sayana</span>
        </div>
        <div className="text-sm text-[#E6E1D6] font-light leading-relaxed max-w-3xl">
          Vision of the Soul — a themed spiritual-astrology reading authored in the voice of a master
          who has internalised <b className="text-[#D4AF37]">Ekkirala Krishnamacharya's</b> Spiritual
          Astrology corpus. Pick any theme below to receive a chart-scoped, five-part reading of your
          soul's current curriculum.
        </div>
      </div>

      {/* Theme grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {themes.map((t) => (
          <button key={t.key}
                  onClick={() => open(t.key)}
                  data-testid={`atma-theme-${t.key}`}
                  className={`text-left border px-3 py-3 rounded-sm transition-all duration-200 font-mono
                    ${themeKey === t.key
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] shadow-[0_0_16px_-6px_rgba(212,175,55,0.4)]"
                      : "border-[#2A2D35] text-[#A19E95] hover:border-[#D4AF37]/50 hover:text-[#E6E1D6] hover:bg-[#181A1F]"}`}>
            <div className="flex items-start gap-1.5">
              <ChevronRight size={12} className="mt-0.5 shrink-0 opacity-60" />
              <div className="text-xs font-medium leading-tight">{t.label}</div>
            </div>
            <div className="text-[9px] text-[#6E6B64] mt-1.5 leading-snug">{t.focus}</div>
          </button>
        ))}
      </div>

      {/* Reading */}
      {loading && (
        <div className="flex items-center gap-2 text-[#A19E95] font-mono text-sm py-6">
          <Loader2 size={14} className="animate-spin" /> Distilling the reading from Master EK's corpus…
        </div>
      )}

      {data && !loading && (
        <div className="border border-[#D4AF37]/30 bg-[#0F1115] p-6 rounded-sm relative overflow-hidden"
             data-testid={`atma-reading-${data.theme}`}>
          <div className="absolute top-0 right-0 flex items-center gap-1 px-3 py-1 bg-[#D4AF37]/10 border-l border-b border-[#D4AF37]/40">
            <Sparkles size={10} className="text-[#D4AF37]" />
            <span className="text-[9px] uppercase tracking-widest font-mono text-[#D4AF37]">gpt-4o-mini · cached</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#A8896C] mb-1">
            Ascendant: {data.ascendant || "?"} · System: {data.system}
          </div>
          {md(data.narrative)}
        </div>
      )}
    </div>
  );
}
